// The shell keyboard model (CONTRACT "Keyboard model"). One window listener,
// single keys inert while a text field has focus, modifiers ignored except
// the Cmd or Ctrl K search focus. Every key in the contract table maps here;
// a key whose handler is absent is inert, so a destination that has no rows
// simply leaves j and k unbound.
//
// KEY_HINTS is the contract's whole table, which is what the "?" sheet must
// print ("the ? sheet lists all of them"); each row carries the scope it
// works in, and a destination merges its own rows through ShellContext.
// STATUS_KEY_CAPS is narrower: the caps the shell itself dispatches, with the
// current destination's caps appended by the status bar.
import { useEffect, useRef } from 'react';
import { isTextInput } from '../ui/isTextInput';

export type ShellKey =
  | 'search' | 'down' | 'up' | 'enter' | 'shiftEnter' | 'escape'
  | 'copy' | 'watch' | 'alert' | 'board'
  | 'list' | 'inspector' | 'tape' | 'help' | 'region' | 'regionBack'
  | 'zoomIn' | 'zoomOut' | 'zoomFit'
  | 'capsOn' | 'capsOff' | 'digit';

export interface KeyHandlers {
  readonly search?: () => void;
  readonly down?: () => void;
  readonly up?: () => void;
  readonly enter?: () => void;
  readonly shiftEnter?: () => void;
  readonly escape?: () => void;
  readonly copy?: () => void;
  readonly watch?: () => void;
  readonly alert?: () => void;
  readonly board?: () => void;
  readonly list?: () => void;
  readonly inspector?: () => void;
  readonly tape?: () => void;
  readonly help?: () => void;
  readonly region?: () => void;
  readonly regionBack?: () => void;
  readonly zoomIn?: () => void;
  readonly zoomOut?: () => void;
  readonly zoomFit?: () => void;
  // Space held shows the numbered caps; releasing it hides them.
  readonly capsOn?: () => void;
  readonly capsOff?: () => void;
  // 1 to 9: research the numbered row.
  readonly digit?: (index: number) => void;
}

export interface KeyHint {
  readonly keys: ReadonlyArray<string>;
  readonly action: string;
  readonly scope?: string;
}

export const KEY_HINTS: ReadonlyArray<KeyHint> = [
  { keys: ['/', 'Cmd K'], action: 'Focus search', scope: 'anywhere' },
  { keys: ['j', 'k'], action: 'Move row focus', scope: 'stream, lane, ledger, list, graph' },
  { keys: ['Enter'], action: 'Select the row and open the inspector', scope: 'lists' },
  { keys: ['Shift Enter'], action: 'Open Coin Checker for the row', scope: 'lists, inspector' },
  { keys: ['Esc'], action: 'Close overlay, then inspector, then selection', scope: 'anywhere' },
  { keys: ['c', 'w', 'a'], action: 'Copy contract, watch or unwatch, alert', scope: 'row, inspector, token page' },
  { keys: ['[', ']', 't'], action: 'Toggle context list, inspector, tape', scope: 'anywhere' },
  { keys: ['b'], action: 'Add a board', scope: 'Live Calls' },
  { keys: ['Space', '1 to 9'], action: 'Hold Space to show numbered caps, then research that row', scope: 'stream, lanes, docked New calls' },
  { keys: ['1 to 9'], action: 'Research the numbered row', scope: 'Coin Checker dock' },
  { keys: ['+', '-', '0'], action: 'Zoom in, zoom out, fit', scope: 'graph' },
  { keys: ['F6', 'Shift F6'], action: 'Cycle regions: top bar, rail, list, workspace, inspector', scope: 'anywhere' },
  { keys: ['?'], action: 'Help sheet', scope: 'anywhere' },
  { keys: ['Tab'], action: 'Native order inside a region', scope: 'anywhere' },
];

// The "?" sheet is one row per key combination. A destination that rewords a
// shell key REPLACES that row in place instead of adding a second wording:
// matching on the wording as well made Live Calls print 21 rows with six keys
// said twice, in different words (redo-1 fix round 1 P1). A destination row
// whose combination is not in the contract table is appended after it, in
// registration order. A Map keeps the contract's order for shared
// combinations and cannot emit a combination twice.
export function mergeKeyHints(
  base: ReadonlyArray<KeyHint>,
  extra: ReadonlyArray<KeyHint>,
): ReadonlyArray<KeyHint> {
  const comboOf = (hint: KeyHint): string => hint.keys.join('+');
  const rows = new Map<string, KeyHint>(base.map((hint) => [comboOf(hint), hint]));
  for (const hint of extra) rows.set(comboOf(hint), hint);
  return [...rows.values()];
}

// The status bar prints the caps the shell itself dispatches; a destination
// adds its own through useShellKeys.
export const STATUS_KEY_CAPS: ReadonlyArray<string> = ['j', 'k', 'Enter', 'c', '[', ']', 't', '?'];

export interface MappedKey {
  readonly key: ShellKey;
  readonly digit?: number;
}

const DIGITS: ReadonlySet<string> = new Set(['1', '2', '3', '4', '5', '6', '7', '8', '9']);

// Maps a keydown to a shell key. Exported so the mapping is unit tested
// without a DOM listener. Returns null when the event is not ours.
export function mapKey(event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>, inField: boolean): MappedKey | null {
  const cmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
  if (cmdK) return { key: 'search' };
  if (event.key === 'Escape') return { key: 'escape' };
  if (event.key === 'F6') return { key: event.shiftKey ? 'regionBack' : 'region' };
  if (inField) return null;
  if (event.metaKey || event.ctrlKey || event.altKey) return null;
  if (event.key === 'Enter') return { key: event.shiftKey ? 'shiftEnter' : 'enter' };
  if (DIGITS.has(event.key)) return { key: 'digit', digit: Number(event.key) };
  switch (event.key) {
    case '/': return { key: 'search' };
    case 'j': return { key: 'down' };
    case 'k': return { key: 'up' };
    case '[': return { key: 'list' };
    case ']': return { key: 'inspector' };
    case 't': return { key: 'tape' };
    case '?': return { key: 'help' };
    case 'c': return { key: 'copy' };
    case 'w': return { key: 'watch' };
    case 'a': return { key: 'alert' };
    case 'b': return { key: 'board' };
    case '+': return { key: 'zoomIn' };
    case '-': return { key: 'zoomOut' };
    case '0': return { key: 'zoomFit' };
    case ' ': return { key: 'capsOn' };
    default: return null;
  }
}

// Space and Enter activate a focused control; the shell never steals them.
function isActivationTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.closest('button, a[href], [role="menuitem"], [role="option"]') !== null;
}

export function useKeys(handlers: KeyHandlers, enabled: boolean = true): void {
  const latest = useRef(handlers);
  useEffect(() => { latest.current = handlers; });
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      const mapped = mapKey(event, isTextInput(event.target));
      if (mapped === null) return;
      // Enter and Space inside a focused control belong to the control.
      if ((mapped.key === 'enter' || mapped.key === 'capsOn') && isActivationTarget(event.target)) return;
      if (mapped.key === 'digit') {
        const digit = latest.current.digit;
        if (!digit || mapped.digit === undefined) return;
        event.preventDefault();
        digit(mapped.digit);
        return;
      }
      const handler = latest.current[mapped.key as Exclude<ShellKey, 'digit'>];
      if (!handler) return;
      if (mapped.key === 'capsOn' && event.repeat) return;
      event.preventDefault();
      handler();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key !== ' ') return;
      latest.current.capsOff?.();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [enabled]);
}
